import { createContext, useContext } from "react";

/**
 * Module-wide grid layout default, provided by the shell for the open page's
 * sidebar module. Lets a whole module adopt a look (e.g. Data Center's G5
 * ListView columns, user request 2026-10-13) without touching each form.
 */
export interface GridLayoutDefaults {
  /** When set, every data column starts at this EXACT width (ellipsis past
   *  it; a user drag still wins), like the legacy XAF ListView. Narrow
   *  icon/action columns (configured width < MIN_DATA_COL_W) are left alone. */
  fixedColumnWidth?: number;
  /** Per-column-key width that beats fixedColumnWidth for columns whose values
   *  are naturally long (e.g. `email`), so they are not cut to "user1@exam…". */
  columnWidths?: Record<string, number>;
  /** Default rows per page for every grid in the module (a form's own explicit
   *  default still wins). */
  pageSize?: number;
  /** With no user sort, show the FIRST column as sorted ascending (the server
   *  applies the same default for the module, so the ▲ tells the truth). */
  defaultSortFirstColumn?: boolean;
}

/** The width a column gets under the module default, or undefined when the
 *  module sets none. */
export function moduleColumnWidth(d: GridLayoutDefaults, key: string): number | undefined {
  return d.columnWidths?.[key] ?? d.fixedColumnWidth;
}

/** Columns configured narrower than this are icon / action columns (edit
 *  pencil, trend button …) — never widened to the module column width. */
export const MIN_DATA_COL_W = 60;

const GridLayoutContext = createContext<GridLayoutDefaults>({});

export const GridLayoutProvider = GridLayoutContext.Provider;

/** The grid layout default for the current module (empty outside one). */
export function useGridLayoutDefaults(): GridLayoutDefaults {
  return useContext(GridLayoutContext);
}
